// Local AI agent integration service.
//
// Lets a user mint/revoke API keys for their own local browser-automation agent
// (e.g. OpenClaw) and enqueue/poll import jobs the agent fulfills. Mirrors
// plaidService's API-root + Supabase-auth-header conventions so it behaves
// identically in local dev, demo mode, and production.

import { supabase } from "../supabase/client";
import { getCachedAccessToken } from "../supabase/authStorage";

const resolveApiRoot = () => {
  if (typeof window !== "undefined") {
    const isLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    if (isLocal) {
      return "/api";
    }
  }
  return process.env.REACT_APP_API_URL
    ? `${process.env.REACT_APP_API_URL}/api`
    : "/api";
};

const API_ROOT = resolveApiRoot();

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const cachedToken = getCachedAccessToken();
  if (cachedToken) {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${cachedToken}`,
    };
  }
  let token: string | undefined;
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    token = session?.access_token;
  } catch (error) {
    console.error("[DEBUG] agentService getAuthHeaders failed", error);
  }
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
};

export interface AgentApiKey {
  id: string;
  name: string;
  key_prefix: string;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

// Returned only by mintKey — `key` is the plaintext, shown to the user once.
export interface MintedAgentKey extends AgentApiKey {
  key: string;
}

export type AgentJobStatus =
  | "queued"
  | "claimed"
  | "running"
  | "completed"
  | "failed";

export interface AgentJob {
  id: string;
  user_id: string;
  type: string;
  status: AgentJobStatus;
  account_id: string | null;
  params: Record<string, unknown>;
  result: { imported?: number; skipped?: number; message?: string } | null;
  progress: string | null;
  error: string | null;
  claimed_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_ROOT}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

export const agentService = {
  async mintKey(name?: string): Promise<MintedAgentKey> {
    console.log("[DEBUG] agentService.mintKey", { name });
    return request<MintedAgentKey>("/agent/keys", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
  },

  async listKeys(): Promise<AgentApiKey[]> {
    const data = await request<{ keys: AgentApiKey[] }>("/agent/keys");
    return data.keys || [];
  },

  async revokeKey(id: string): Promise<void> {
    console.log("[DEBUG] agentService.revokeKey", { id });
    await request(`/agent/keys/${id}`, { method: "DELETE" });
  },

  async enqueueJob(input: {
    type?: string;
    account_id: string;
    params?: Record<string, unknown>;
  }): Promise<AgentJob> {
    console.log("[DEBUG] agentService.enqueueJob", input);
    const data = await request<{ job: AgentJob }>("/agent/jobs", {
      method: "POST",
      body: JSON.stringify({ type: "cashapp_import", ...input }),
    });
    return data.job;
  },

  async getJob(id: string): Promise<AgentJob> {
    const data = await request<{ job: AgentJob }>(`/agent/jobs/${id}`);
    return data.job;
  },

  // Poll a job until it reaches a terminal state (or the caller aborts).
  async waitForJob(
    id: string,
    opts: {
      intervalMs?: number;
      timeoutMs?: number;
      onUpdate?: (job: AgentJob) => void;
      signal?: AbortSignal;
    } = {}
  ): Promise<AgentJob> {
    const intervalMs = opts.intervalMs ?? 3000;
    const timeoutMs = opts.timeoutMs ?? 5 * 60 * 1000;
    const start = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (opts.signal?.aborted) throw new Error("aborted");
      const job = await this.getJob(id);
      opts.onUpdate?.(job);
      if (job.status === "completed" || job.status === "failed") return job;
      if (Date.now() - start > timeoutMs) {
        throw new Error("Timed out waiting for agent job");
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }
  },
};

export default agentService;
